import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Spin, Table } from "antd";
import type { TablePaginationConfig, TableProps } from "antd";
import NoDataComponent from "../NoData";
import { DatatableProps } from "../../../core/data/interface";
import Loader from "../loader";

type BackendPagination = Pick<TablePaginationConfig, "current" | "pageSize" | "total"> & {
  showSizeChanger?: boolean;
  pageSizeOptions?: string[];
};

type Props = DatatableProps & {
  /** If true, the table shows a pagination UI using numbers from `dataPaginate` */
  pagination?: boolean;
  /** Backend pagination numbers (current, pageSize, total) */
  dataPaginate?: BackendPagination;
  /** Pass through AntD's onChange when you want to react to sort/paginate in parent */
  onChange?: TableProps<any>["onChange"];
  /** Row key field (default "id") */
  rowKey?: string | ((record: any) => string);
  expandable?: TableProps<any>["expandable"];
  defaultExpandedRowKeys?: TableProps<any>["defaultExpandedRowKeys"];
  scroll?: TableProps<any>["scroll"];
  sticky?: TableProps<any>["sticky"];

};

const Datatable: React.FC<Props> = ({
  columns,
  dataSource = [],
  Selection,
  bordered,
  pagination = false,
  expandable,
  dataPaginate,
  loading = false,
  onChange,
  rowKey = "id",
  defaultExpandedRowKeys,
  scroll,
  sticky,
}) => {
  const { t } = useTranslation();

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [Selections, setSelections] = useState<boolean>(!!Selection);

  // Keep Selections in sync with prop
  useEffect(() => {
    setSelections(!!Selection);
  }, [Selection]);

  // Clear selection whenever the data set changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [dataSource]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    }),
    [selectedRowKeys]
  );

  const isBackendMode = !!dataPaginate;

  /**
   * Build pagination config:
   * - Backend mode: controlled (use numbers from `dataPaginate`)
   * - Client mode: uncontrolled (let AntD manage current/pageSize)
   */
  const paginationConfig: TablePaginationConfig | false = useMemo(() => {
    if (!pagination) return false;

    const base: Partial<TablePaginationConfig> = {
      showSizeChanger: dataPaginate?.showSizeChanger ?? true,
      pageSizeOptions: dataPaginate?.pageSizeOptions ?? ["5", "10", "20", "50", "100"],
      showTotal: (tot, range) => `${t("showing")} ${range[0]}-${range[1]} ${t("of")} ${tot}`,
    };

    if (isBackendMode) {
      // Controlled pagination (API-driven)
      const { current = 1, pageSize = 10, total = 0 } = dataPaginate!;
      return {
        ...base,
        current,
        pageSize,
        total,
      } as TablePaginationConfig;
    }

    // Client-side pagination (uncontrolled): DON'T set current/pageSize.
    // AntD will paginate dataSource on the client.
    return {
      ...base,
      total: dataSource.length, // optional, helps showTotal be accurate
    } as TablePaginationConfig;
  }, [pagination, dataPaginate, dataSource.length, isBackendMode, t]);

  // Wrap onChange: pass through to parent unchanged
  // (Parent will only react in backend mode; in client mode, it's optional.)
  const handleChange: TableProps<any>["onChange"] = (pg, filters, sorter, extra) => {
    onChange?.(pg, filters, sorter, extra);
  };

  if (loading) {
    return <Loader />;
  }

  const commonProps = {
    className: "table datanew dataTable  no-footer",
    columns,
    rowKey,
    rowHoverable: false as const,
    bordered,
    dataSource,
    locale: { emptyText: <NoDataComponent /> },
    pagination: paginationConfig,
    onChange: handleChange,
    expandable,
    defaultExpandedRowKeys,
    scroll,
    sticky,

  };

  return (
    <>
      <div className="table-top-data d-flex px-3 justify-content-between" />
      {!Selections ? (
        <Table {...commonProps} />
      ) : (
        <Table {...commonProps} rowSelection={rowSelection} />
      )}
    </>
  );
};

export default Datatable;
